import { useCallback } from "react";
import { FormikProps } from "formik";
import _ from "lodash";

import { format, FORMATS, IDate } from "utils/datetime";

import Stack from "components/Stack";

import { TRANSACTION_CREDIT_TYPE_OPTIONS } from "pages/Deal/FinancingEvents/constants";
import TransactionDatePicker from "pages/Deal/FinancingEvents/TransactionFields/TransactionDatePickerField";
import TransactionSelectField from "pages/Deal/FinancingEvents/TransactionFields/TransactionSelectField";
import TransactionTextField from "pages/Deal/FinancingEvents/TransactionFields/TransactionTextField";
import TransactionTypeSpecificHeader from "pages/Deal/FinancingEvents/TransactionModifyDialog/TransactionForm/TransactionTypeSpecificHeader";

import { ITransactionValuesForm } from "entities/Deal/sdk";

export interface ITransactionTaxCreditAllocationModifyForm {
  formik: FormikProps<ITransactionValuesForm>;
  lastUpdatedInformation: string | null;
  transactionIdentifier?: string;
}

const TransactionTaxCreditAllocationModifyForm = ({
  formik,
  transactionIdentifier,
  lastUpdatedInformation,
}: ITransactionTaxCreditAllocationModifyForm) => {
  const onChangeDatePicker = useCallback(
    ({ value, fieldName }: { value: IDate | null; fieldName: string }) => {
      if (!_.isNil(value)) {
        formik.setFieldValue(
          `tax_credit_allocation_transaction.${fieldName}`,
          format(value, FORMATS.BACKEND.DATE)
        );
      } else {
        formik.setFieldValue(
          `tax_credit_allocation_transaction.${fieldName}`,
          null
        );
      }
    },
    [formik]
  );

  return (
    <>
      <TransactionTypeSpecificHeader
        transactionIdentifier={transactionIdentifier}
        transactionType={formik.values.transaction_type}
        lastUpdatedInformation={lastUpdatedInformation}
      />
      <Stack
        gap={1}
        rowGap={3}
        padding={2}
        marginTop={2}
        gridTemplateColumns="1fr 1fr"
        display="grid"
      >
        <TransactionSelectField
          formik={formik}
          options={TRANSACTION_CREDIT_TYPE_OPTIONS}
          inputLabel="Credit type"
          fieldName="tax_credit_allocation_transaction.credit_type"
        />
        <TransactionTextField
          fieldName="tax_credit_allocation_transaction.credit_amount_10_year"
          label="Credit amount for 10 years"
          isNumber
          formik={formik}
          showDollarSign
        />
        <TransactionDatePicker
          formik={formik}
          onChange={(value) =>
            onChangeDatePicker({
              value,
              fieldName: "first_year_credit_period_per_8609",
            })
          }
          fieldName="first_year_credit_period_per_8609"
          label="First year credit period (per 8609)"
        />
        <TransactionDatePicker
          formik={formik}
          onChange={(value) => {
            onChangeDatePicker({
              value,
              fieldName: "regulatory_approval_date",
            });
          }}
          fieldName="regulatory_approval_date"
          label="Regulatory approval date"
        />
        <TransactionTextField
          fieldName="tax_credit_allocation_transaction.housing_credit_equity"
          label="Housing credit equity"
          isNumber
          formik={formik}
          showDollarSign
        />

        <TransactionTextField
          fieldName="tax_credit_allocation_transaction.equity_amount"
          label="Equity amount"
          isNumber
          formik={formik}
          showDollarSign
        />
        <TransactionTextField
          fieldName="tax_credit_allocation_transaction.equity_provider_fund_name"
          label="Equity provider fund name"
          formik={formik}
        />
        <TransactionTextField
          fieldName="tax_credit_allocation_transaction.equity_provider"
          label="Equity provider"
          formik={formik}
        />
        <TransactionTextField
          fieldName="tax_credit_allocation_transaction.price_per_credit"
          label="Price per credit"
          isNumber
          formik={formik}
          showDollarSign
        />

        <TransactionDatePicker
          formik={formik}
          onChange={(value) =>
            onChangeDatePicker({
              value,
              fieldName: "placed_in_service_date",
            })
          }
          fieldName="placed_in_service_date"
          label="Placed in service date"
        />
        <TransactionTextField
          fieldName="tax_credit_allocation_transaction.compliance_period"
          label="Compliance period (years)"
          isNumber
          formik={formik}
          sx={{ gridColumn: "1 / -1" }}
        />
      </Stack>
    </>
  );
};

export default TransactionTaxCreditAllocationModifyForm;
